import type {
  ClientLibraryContentGroupInput,
  ClientLibraryContentItem,
} from 'types/client-library'
import type { Group, GroupId } from 'types/group'

export type SelectedGroups = Record<GroupId, 'checked' | 'partial'>

export type ChannelIndex = {
  itemsById: Map<GroupId, ClientLibraryContentItem>
  childrenByParent: Map<GroupId, GroupId[]>
  rootIds: GroupId[]
}

export type ChannelRow = ClientLibraryContentItem & {
  folderName?: string
  key?: React.Key
}

export type AutoSyncEntry = {
  auto_sync_playlists: boolean
  auto_sync_albums: boolean
}

export type AutoSyncState = Record<GroupId, AutoSyncEntry>
export type AutoSyncField = 'auto_sync_playlists' | 'auto_sync_albums'

export const buildIndex = (
  items: ClientLibraryContentItem[],
): ChannelIndex => {
  const itemsById = new Map<GroupId, ClientLibraryContentItem>()
  const childrenByParent = new Map<GroupId, GroupId[]>()
  const rootIds: GroupId[] = []

  items.forEach(item => {
    itemsById.set(item.id, item)
  })

  items.forEach(item => {
    if (item.parent != null && itemsById.has(item.parent)) {
      const bucket = childrenByParent.get(item.parent) ?? []
      bucket.push(item.id)
      childrenByParent.set(item.parent, bucket)
    } else {
      rootIds.push(item.id)
    }
  })

  return { itemsById, childrenByParent, rootIds }
}

export const childrenOf = (id: GroupId, index: ChannelIndex): GroupId[] =>
  index.childrenByParent.get(id) ?? []

export const descendantsOf = (id: GroupId, index: ChannelIndex): GroupId[] => {
  const result: GroupId[] = []
  const queue: GroupId[] = [...childrenOf(id, index)]
  const seen = new Set<GroupId>(queue)
  while (queue.length) {
    const next = queue.shift()!
    result.push(next)
    for (const childId of childrenOf(next, index)) {
      if (!seen.has(childId)) {
        seen.add(childId)
        queue.push(childId)
      }
    }
  }
  return result
}

export const depthOf = (id: GroupId, index: ChannelIndex): number => {
  let depth = 0
  const seen = new Set<GroupId>()
  let cursor = index.itemsById.get(id)?.parent
  while (cursor != null && index.itemsById.has(cursor) && !seen.has(cursor)) {
    seen.add(cursor)
    depth++
    cursor = index.itemsById.get(cursor)?.parent
  }
  return depth
}

export const expandFolders = (
  id: GroupId,
  name: Group['name'],
  index: ChannelIndex,
  expandedGroups: GroupId[],
): ChannelRow[] => {
  const result: ChannelRow[] = []

  childrenOf(id, index).forEach(childId => {
    const child = index.itemsById.get(childId)
    if (!child) {
      return
    }

    result.push({ ...child, folderName: name, key: child.id })
    if (expandedGroups.includes(childId)) {
      result.push(...expandFolders(childId, name, index, expandedGroups))
    }
  })

  return result
}

export const getSelectedGroups = (
  libraryContent: GroupId[],
  index: ChannelIndex,
): SelectedGroups => {
  const result: SelectedGroups = {}

  libraryContent.forEach(id => {
    result[id] = 'checked'
  })

  libraryContent.forEach(id => {
    const seen = new Set<GroupId>()
    let cursor = index.itemsById.get(id)?.parent
    while (cursor != null && index.itemsById.has(cursor) && !seen.has(cursor)) {
      seen.add(cursor)
      if (result[cursor] !== 'checked') {
        result[cursor] = 'partial'
      }
      cursor = index.itemsById.get(cursor)?.parent
    }
  })

  return result
}

export const computeDiff = (
  libraryContent: GroupId[],
  autoSync: AutoSyncState,
  savedContent: GroupId[],
  savedSettings: AutoSyncState,
): {
  toAdd: ClientLibraryContentGroupInput[]
  toRemove: GroupId[]
} => {
  const savedSet = new Set(savedContent)
  const currentSet = new Set(libraryContent)
  const toRemove: GroupId[] = savedContent.filter(id => !currentSet.has(id))
  const toAdd: ClientLibraryContentGroupInput[] = libraryContent
    .filter(id => {
      const current = autoSync[id] ?? {
        auto_sync_playlists: false,
        auto_sync_albums: false,
      }
      const saved = savedSettings[id]

      if (!savedSet.has(id)) {
        return true
      }

      return (
        !saved ||
        saved.auto_sync_playlists !== current.auto_sync_playlists ||
        saved.auto_sync_albums !== current.auto_sync_albums
      )
    })
    .map(id => {
      const current = autoSync[id] ?? {
        auto_sync_playlists: false,
        auto_sync_albums: false,
      }
      return {
        id,
        auto_sync_playlists: current.auto_sync_playlists,
        auto_sync_albums: current.auto_sync_albums,
      }
    })
  return { toAdd, toRemove }
}
